import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, addDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../services/firebase/config';

interface Dress {
  id: string;
  name: string;
  description: string;
  size: string;
  price: number;
  imageURL: string;
  status: 'available' | 'rented' | 'unavailable';
  location: string;
}

const ClosetTab: React.FC = () => {
  const { user } = useAuth();
  const [dresses, setDresses] = useState<Dress[]>([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [newDress, setNewDress] = useState({
    name: '',
    description: '',
    size: '',
    price: '',
    location: '',
    status: 'available' as const
  });

  useEffect(() => {
    loadDresses();
  }, [user]);

  const loadDresses = async () => {
    if (!user) return;
    try {
      const q = query(collection(db, 'dresses'), where('userId', '==', user.uid));
      const querySnapshot = await getDocs(q);
      const dressesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Dress[];
      setDresses(dressesData);
    } catch (error) {
      console.error('Error loading dresses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setSelectedImages(Array.from(event.target.files));
    }
  };

  const handleAddDress = async () => {
    if (!user || selectedImages.length === 0) return;

    try {
      // Upload all images
      const imageUrls: string[] = [];
      for (const file of selectedImages) {
        const storageRef = ref(storage, `dresses/${user.uid}/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        imageUrls.push(url);
      }

      // Add dress to Firestore
      const dressData = {
        ...newDress,
        price: Number(newDress.price),
        imageURLs: imageUrls,
        userId: user.uid,
        createdAt: new Date()
      };

      await addDoc(collection(db, 'dresses'), dressData);
      setOpenModal(false);
      setNewDress({
        name: '',
        description: '',
        size: '',
        price: '',
        location: '',
        status: 'available'
      });
      setSelectedImages([]);
      loadDresses();
    } catch (error) {
      console.error('Error adding dress:', error);
    }
  };

  const handleDeleteDress = async (dressId: string) => {
    if (!user) return;
    try {
      await deleteDoc(doc(db, 'dresses', dressId));
      loadDresses();
    } catch (error) {
      console.error('Error deleting dress:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ color: '#C9B6AB' }}>
          Mon Dressing
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenModal(true)}
          sx={{
            backgroundColor: '#C9B6AB',
            '&:hover': {
              backgroundColor: '#C1A08E',
            },
          }}
        >
          Ajouter une robe
        </Button>
      </Box>

      <Grid container spacing={3}>
        {dresses.map((dress) => (
          <Grid item xs={12} sm={6} md={4} key={dress.id}>
            <Card>
              <CardMedia
                component="img"
                height="200"
                image={dress.imageURL}
                alt={dress.name}
              />
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {dress.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {dress.description}
                </Typography>
                <Typography variant="body2">
                  Taille: {dress.size}
                </Typography>
                <Typography variant="body2">
                  Prix: {dress.price}€/jour
                </Typography>
                <Typography variant="body2">
                  📍 {dress.location}
                </Typography>
                <Typography variant="body2" sx={{ color: dress.status === 'available' ? 'green' : 'red' }}>
                  {dress.status === 'available' ? 'Disponible' : 'Non disponible'}
                </Typography>
              </CardContent>
              <CardActions>
                <IconButton size="small" color="primary">
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteDress(dress.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter une robe</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Nom de la robe"
              value={newDress.name}
              onChange={(e) => setNewDress({ ...newDress, name: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Description"
              value={newDress.description}
              onChange={(e) => setNewDress({ ...newDress, description: e.target.value })}
              margin="normal"
              multiline
              rows={3}
            />
            <TextField
              fullWidth
              label="Taille"
              value={newDress.size}
              onChange={(e) => setNewDress({ ...newDress, size: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Prix par jour (€)"
              type="number"
              value={newDress.price}
              onChange={(e) => setNewDress({ ...newDress, price: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Localisation"
              value={newDress.location}
              onChange={(e) => setNewDress({ ...newDress, location: e.target.value })}
              margin="normal"
              placeholder="Ex: Paris 15ème"
            />
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mt: 2 }}
            >
              Ajouter des photos
              <input
                type="file"
                hidden
                accept="image/*"
                multiple
                onChange={handleImageChange}
              />
            </Button>
            {selectedImages.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {selectedImages.map((file, idx) => (
                  <Box key={idx} sx={{ width: 80, height: 80, border: '1px solid #eee', borderRadius: 2, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Annuler</Button>
          <Button
            onClick={handleAddDress}
            variant="contained"
            disabled={selectedImages.length === 0 || !newDress.name || !newDress.price}
            sx={{
              backgroundColor: '#C9B6AB',
              '&:hover': {
                backgroundColor: '#C1A08E',
              },
            }}
          >
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClosetTab; 